import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Goal, GoalProgress, Run } from '../types';
import { runService } from './runService';
import { Capacitor } from '@capacitor/core';
import { firestoreRestApi } from '../utils/firestoreRestApi';

export const goalService = {
  // Create a new goal
  async createGoal(goalData: Omit<Goal, 'id' | 'createdAt' | 'updatedAt' | 'currentValue' | 'isCompleted'>): Promise<Goal> {
    const goalRef = await addDoc(collection(db, 'goals'), {
      ...goalData,
      currentValue: 0,
      isCompleted: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });

    const goal = {
      id: goalRef.id,
      ...goalData,
      currentValue: 0,
      isCompleted: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    } as Goal;

    // Calculate initial progress
    await this.updateGoalProgress(goal.id);
    
    return goal;
  },

  // Get goals for an athlete
  async getGoalsByAthlete(athleteId: string): Promise<Goal[]> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'goals',
          [{ field: 'athleteId', op: '==', value: athleteId }]
        );
        
        // Sort by createdAt descending (client-side)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().createdAt?.seconds || 0;
          const bTime = b.data().createdAt?.seconds || 0;
          return bTime - aTime;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt || Timestamp.now(),
          updatedAt: doc.data().updatedAt || Timestamp.now(),
          startDate: doc.data().startDate || Timestamp.now(),
          endDate: doc.data().endDate || Timestamp.now(),
        })) as Goal[];
      } catch (error) {
        console.error('Error fetching goals via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    const q = query(
      collection(db, 'goals'),
      where('athleteId', '==', athleteId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt,
      updatedAt: doc.data().updatedAt,
      startDate: doc.data().startDate,
      endDate: doc.data().endDate,
    })) as Goal[];
  },

  // Get goals for a team (for coaches)
  async getGoalsByTeam(teamId: string): Promise<Goal[]> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'goals',
          [{ field: 'teamId', op: '==', value: teamId }]
        );
        
        // Sort by createdAt descending (client-side)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().createdAt?.seconds || 0;
          const bTime = b.data().createdAt?.seconds || 0;
          return bTime - aTime;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt || Timestamp.now(),
          updatedAt: doc.data().updatedAt || Timestamp.now(),
          startDate: doc.data().startDate || Timestamp.now(),
          endDate: doc.data().endDate || Timestamp.now(),
        })) as Goal[];
      } catch (error) {
        console.error('Error fetching team goals via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    const q = query(
      collection(db, 'goals'),
      where('teamId', '==', teamId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt,
      updatedAt: doc.data().updatedAt,
      startDate: doc.data().startDate,
      endDate: doc.data().endDate,
    })) as Goal[];
  },

  // Get a single goal by ID
  async getGoalById(goalId: string): Promise<Goal | null> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const goalDoc = await firestoreRestApi.getDocument('goals', goalId);
        if (!goalDoc) return null;
        
        return {
          id: goalDoc.id,
          ...goalDoc.data(),
          createdAt: goalDoc.data().createdAt || Timestamp.now(),
          updatedAt: goalDoc.data().updatedAt || Timestamp.now(),
          startDate: goalDoc.data().startDate || Timestamp.now(),
          endDate: goalDoc.data().endDate || Timestamp.now(),
        } as Goal;
      } catch (error) {
        console.error('Error fetching goal via REST API:', error);
        return null;
      }
    }
    
    // On web/Android, use SDK
    const goalDoc = await getDoc(doc(db, 'goals', goalId));
    
    if (!goalDoc.exists()) return null;
    
    return {
      id: goalDoc.id,
      ...goalDoc.data(),
      createdAt: goalDoc.data().createdAt,
      updatedAt: goalDoc.data().updatedAt,
      startDate: goalDoc.data().startDate,
      endDate: goalDoc.data().endDate,
    } as Goal;
  },

  // Update a goal
  async updateGoal(goalId: string, updates: Partial<Omit<Goal, 'id' | 'createdAt' | 'updatedAt'>>): Promise<void> {
    const goalRef = doc(db, 'goals', goalId);
    await updateDoc(goalRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });

    // Recalculate progress after update
    await this.updateGoalProgress(goalId);
  },

  // Delete a goal
  async deleteGoal(goalId: string): Promise<void> {
    await deleteDoc(doc(db, 'goals', goalId));
  },

  // Update goal progress based on runs
  async updateGoalProgress(goalId: string): Promise<void> {
    const goal = await this.getGoalById(goalId);
    if (!goal) return;

    const runs = await runService.getRunsByAthlete(goal.athleteId);
    const goalStartDate = goal.startDate.toDate();
    const goalEndDate = goal.endDate.toDate();

    // Filter runs within goal period
    const relevantRuns = runs.filter(run => {
      const runDate = run.createdAt.toDate();
      return runDate >= goalStartDate && runDate <= goalEndDate;
    });

    let currentValue = 0;
    let isCompleted = false;

    switch (goal.goalType) {
      case 'distance':
        currentValue = relevantRuns.reduce((sum, run) => sum + run.distance, 0);
        isCompleted = currentValue >= goal.targetValue;
        break;
      
      case 'time':
        currentValue = relevantRuns.reduce((sum, run) => sum + run.time, 0);
        isCompleted = currentValue >= goal.targetValue;
        break;
      
      case 'pace':
        if (relevantRuns.length > 0) {
          const totalDistance = relevantRuns.reduce((sum, run) => sum + run.distance, 0);
          const totalTime = relevantRuns.reduce((sum, run) => sum + run.time, 0);
          currentValue = totalDistance > 0 ? totalTime / totalDistance : 0; // minutes per mile
        }
        isCompleted = currentValue <= goal.targetValue; // Lower pace is better
        break;
      
      case 'frequency':
        // Count unique days with runs
        const uniqueDays = new Set(
          relevantRuns.map(run => run.createdAt.toDate().toDateString())
        ).size;
        currentValue = uniqueDays;
        isCompleted = currentValue >= goal.targetValue;
        break;
      
      case 'custom':
        // For custom goals, we'll need to implement custom logic
        // For now, just use distance as default
        currentValue = relevantRuns.reduce((sum, run) => sum + run.distance, 0);
        isCompleted = currentValue >= goal.targetValue;
        break;
    }

    // Update the goal with new progress
    const goalRef = doc(db, 'goals', goalId);
    await updateDoc(goalRef, {
      currentValue,
      isCompleted,
      updatedAt: Timestamp.now(),
    });
  },

  // Get goal progress history
  async getGoalProgress(goalId: string): Promise<GoalProgress[]> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const documents = await firestoreRestApi.queryDocuments(
          'goalProgress',
          [{ field: 'goalId', op: '==', value: goalId }]
        );
        
        // Sort by createdAt descending (client-side)
        const sorted = documents.sort((a, b) => {
          const aTime = a.data().createdAt?.seconds || 0;
          const bTime = b.data().createdAt?.seconds || 0;
          return bTime - aTime;
        });
        
        return sorted.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt || Timestamp.now(),
          periodStart: doc.data().periodStart || Timestamp.now(),
          periodEnd: doc.data().periodEnd || Timestamp.now(),
        })) as GoalProgress[];
      } catch (error) {
        console.error('Error fetching goal progress via REST API:', error);
        return [];
      }
    }
    
    // On web/Android, use SDK
    const q = query(
      collection(db, 'goalProgress'),
      where('goalId', '==', goalId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt,
      periodStart: doc.data().periodStart,
      periodEnd: doc.data().periodEnd,
    })) as GoalProgress[];
  },

  // Calculate goal progress percentage
  calculateProgressPercentage(goal: Goal): number {
    if (goal.targetValue === 0) return 0;
    
    if (goal.goalType === 'pace') {
      // For pace goals, lower is better, so we calculate differently
      if (goal.currentValue === 0) return 0;
      const progress = Math.max(0, (goal.targetValue - goal.currentValue) / goal.targetValue * 100);
      return Math.min(100, progress);
    }
    
    const progress = (goal.currentValue / goal.targetValue) * 100;
    return Math.min(100, Math.max(0, progress));
  },

  // Get goal status text
  getGoalStatusText(goal: Goal): string {
    if (goal.isCompleted) return 'Completed';
    
    const progress = this.calculateProgressPercentage(goal);
    if (progress === 0) return 'Not started';
    if (progress < 25) return 'Getting started';
    if (progress < 50) return 'Making progress';
    if (progress < 75) return 'Good progress';
    if (progress < 100) return 'Almost there';
    
    return 'Completed';
  },

  // Format goal value for display
  formatGoalValue(value: number, unit: string, goalType: string): string {
    if (goalType === 'pace') {
      const minutes = Math.floor(value);
      const seconds = Math.round((value - minutes) * 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')} ${unit}`;
    }
    
    if (goalType === 'time') {
      return runService.formatTime(value);
    }
    
    return `${value.toFixed(1)} ${unit}`;
  },

  // Get goal type options
  getGoalTypeOptions() {
    return [
      { value: 'distance', label: 'Total Distance', unit: 'miles', description: 'Run a total distance' },
      { value: 'time', label: 'Total Time', unit: 'minutes', description: 'Run for a total time' },
      { value: 'pace', label: 'Average Pace', unit: 'min/mile', description: 'Achieve a target pace' },
      { value: 'frequency', label: 'Run Frequency', unit: 'days', description: 'Run on a certain number of days' },
      { value: 'custom', label: 'Custom Goal', unit: 'units', description: 'Set a custom goal' },
    ];
  },
};
